import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSharedCourse } from '../hooks/useShared'
import { useAuthStore, selectIsAuthenticated } from '../stores/authStore'
import { useLocale } from '../i18n/context'

export default function SharedCoursePage() {
  const { tr } = useLocale()
  const sp = tr.sharedPages
  const lib = tr.library
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore(selectIsAuthenticated)

  const { data: course, isLoading, isError } = useSharedCourse(token!)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())

  function toggleFolder(id: string) {
    setExpandedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (isLoading) return <SharedPageSkeleton />
  if (isError || !course) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{sp.courseNotFound}</h2>
          <p style={{ margin: 0, fontSize: 14 }}>{sp.linkExpired}</p>
        </div>
      </div>
    )
  }

  const totalDecks = course.folders.reduce((sum, f) => sum + f.decks.length, 0) + course.standalone_decks.length

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
      {/* Top bar */}
      <div style={{ background: 'var(--bg-header)', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: 'white', letterSpacing: '-0.01em' }}>{'CardSmith' /* i18n-exempt: brand name */}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/login')} style={{ padding: '8px 16px', background: 'transparent', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 'var(--radius-pill)', fontSize: 13, cursor: 'pointer' }}>{sp.logIn}</button>
          <button onClick={() => navigate('/register')} style={{ padding: '8px 16px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{sp.signUpFree}</button>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px' }}>
        {/* Course header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ margin: '0 0 6px', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>{course.title}</h1>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
            {lib.deckCount(totalDecks)}
            {course.folders.length > 0 && ` · ${lib.folderCount(course.folders.length)}`}
          </p>
          {course.description && <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>{course.description}</p>}
        </div>

        {/* Sign-up banner */}
        {!isAuthenticated && (
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '20px 24px', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', boxShadow: 'var(--shadow-card)' }}>
            <div>
              <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{sp.studyCourseQ}</p>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{sp.studyCourseMsg}</p>
            </div>
            <button onClick={() => navigate('/register')} style={{ padding: '10px 24px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
              {sp.signUpFreeArrow}
            </button>
          </div>
        )}

        {/* Content */}
        <h2 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{sp.courseContent}</h2>

        {/* Folders */}
        {course.folders.map((folder) => {
          const isOpen = expandedFolders.has(folder.id)
          return (
            <div key={folder.id} style={{ marginBottom: 8 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--bg-surface)', borderRadius: isOpen ? '12px 12px 0 0' : 'var(--radius-sm)', border: '1px solid var(--border)', cursor: 'pointer' }}
                onClick={() => toggleFolder(folder.id)}
              >
                <span style={{ fontSize: 14, transition: 'transform 0.15s', display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                <span style={{ fontSize: 16 }}>📁</span>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{folder.title}</span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{lib.deckCount(folder.decks.length)}</span>
              </div>
              {isOpen && (
                <div style={{ border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
                  {folder.decks.map((deck, i) => (
                    <div key={deck.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px 10px 44px', background: 'var(--bg-secondary)', borderBottom: i < folder.decks.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <span style={{ fontSize: 14 }}>🃏</span>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{deck.title}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{sp.cardCount(deck.card_count)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}

        {/* Standalone decks */}
        {course.standalone_decks.length > 0 && (
          <div style={{ marginTop: course.folders.length > 0 ? 8 : 0 }}>
            {course.folders.length > 0 && (
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '16px 0 8px' }}>{sp.standaloneDecks}</p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {course.standalone_decks.map((deck) => (
                <div key={deck.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                  <span style={{ fontSize: 14 }}>🃏</span>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{deck.title}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{sp.cardCount(deck.card_count)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function SharedPageSkeleton() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
      <div style={{ background: 'var(--bg-header)', height: 56 }} />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{ height: 36, width: '40%', background: 'var(--border)', borderRadius: 8, marginBottom: 12 }} />
        <div style={{ height: 14, width: '25%', background: 'var(--border)', borderRadius: 4, marginBottom: 32 }} />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{ height: 48, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8 }} />
        ))}
      </div>
    </div>
  )
}
